<template>
  <div v-show="isResourceLayerVisibleOnMap" class="MyMapResourceFilters">
    <div class="MyMapResourceFilters__titleBar" role="presentation">
      <span class="MyMapResourceFilters__titleText">{{
        $t('myMap.leftSidebar.resourceFilters.filterBar')
      }}</span>
    </div>
    <MyMapPlatformItemSearch :item-type="ItemType.RESOURCE" />
    <div class="MyMapResourceFilters__fields">
      <label class="MyMapResourceFilters__label">
        <span class="MyMapResourceFilters__fieldCaption">{{
          $t('myMap.leftSidebar.resourceFilters.type')
        }}</span>
        <select
          v-model="myMapStore.resourceMapFilters.type"
          :class="[
            'MyMapResourceFilters__input',
            { 'MyMapResourceFilters__input--empty': !myMapStore.resourceMapFilters.type },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.resourceFilters.optionAll') }}</option>
          <option v-for="opt in resourceFilterOptions.type" :key="opt" :value="opt">
            {{ resourceTypeLabel(opt) }}
          </option>
        </select>
      </label>
      <label class="MyMapResourceFilters__label">
        <span class="MyMapResourceFilters__fieldCaption">{{
          $t('myMap.leftSidebar.resourceFilters.administrativeScopes')
        }}</span>
        <select
          v-model="myMapStore.resourceMapFilters.administrativeScopes"
          :class="[
            'MyMapResourceFilters__input',
            {
              'MyMapResourceFilters__input--empty':
                !myMapStore.resourceMapFilters.administrativeScopes,
            },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.resourceFilters.optionAll') }}</option>
          <option v-for="opt in resourceFilterOptions.administrativeScopes" :key="opt" :value="opt">
            {{ administrativeScopeLabel(opt) }}
          </option>
        </select>
      </label>
      <label class="MyMapResourceFilters__label">
        <span class="MyMapResourceFilters__fieldCaption">{{
          $t('myMap.leftSidebar.resourceFilters.thematics')
        }}</span>
        <select
          v-model="myMapStore.resourceMapFilters.thematics"
          :class="[
            'MyMapResourceFilters__input',
            { 'MyMapResourceFilters__input--empty': !myMapStore.resourceMapFilters.thematics },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.resourceFilters.optionAll') }}</option>
          <option v-for="opt in resourceFilterOptions.thematics" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
      </label>
    </div>
    <button type="button" class="MyMapResourceFilters__clear" @click="onClearFilters">
      {{ $t('myMap.leftSidebar.resourceFilters.clear') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ItemType } from '@/models/enums/app/ItemType'
import {
  createEmptyResourceFilterOptions,
  type ResourceFilterOptions,
} from '@/models/interfaces/map/PlatformFilterOptions'
import { AppLayersService } from '@/services/map/AppLayersService'
import MyMapPlatformItemSearch from '@/views/map/components/MyMapPlatformItemSearch.vue'
import { ResourceService } from '@/services/resources/ResourceService'
import { useMyMapStore } from '@/stores/myMapStore'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'

const myMapStore = useMyMapStore()
const { t, te } = useI18n()

const resourceFilterOptions = reactive<ResourceFilterOptions>(createEmptyResourceFilterOptions())

const isResourceLayerVisibleOnMap = computed(
  () => myMapStore.resourceLayer?.isShown === true,
)

const resourceFilterOptionsLoaded = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | undefined

function resourceTypeLabel(value: string): string {
  const key = `resources.resourceType.${value}`
  return te(key) ? String(t(key)) : value
}

function administrativeScopeLabel(value: string): string {
  const key = `projects.scope.${value}`
  return te(key) ? String(t(key)) : value
}

async function refreshResourceLayer(): Promise<void> {
  const myMap = myMapStore.myMap
  if (!myMap) return
  const mapInstance = (myMap as { map?: { getSource: (id: string) => unknown } }).map
  const sourceExists = mapInstance?.getSource?.(ItemType.RESOURCE)
  if (!sourceExists && (myMapStore.resourceLayer?.isShown ?? false)) {
    await AppLayersService.setPlatformDataLayer(ItemType.RESOURCE)
  }
  myMap.setData(ItemType.RESOURCE, AppLayersService.getGeojsonForMapDisplay(ItemType.RESOURCE))
}

async function applyFiltersAndRefresh(): Promise<void> {
  await myMapStore.applyResourceMapFilters()
  await refreshResourceLayer()
}

function onClearFilters(): void {
  myMapStore.clearResourceMapFilters()
  void refreshResourceLayer()
}

watch(
  isResourceLayerVisibleOnMap,
  async (visible) => {
    if (!visible || resourceFilterOptionsLoaded.value) return
    const loaded = await ResourceService.getFilterOptions()
    Object.assign(resourceFilterOptions, loaded)
    resourceFilterOptionsLoaded.value = true
  },
  { immediate: true },
)

watch(
  () => myMapStore.resourceMapFilters,
  () => {
    if (!isResourceLayerVisibleOnMap.value) return
    if (debounceTimer != null) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      void applyFiltersAndRefresh()
    }, 400)
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer)
  }
})
</script>

<style scoped lang="scss">
.MyMapResourceFilters {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 0.5rem;
  padding-top: 0;
}

.MyMapResourceFilters__titleBar {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.5rem;
  margin: 0.125rem 0 0.25rem;
  border-radius: 0.5rem;
  background: rgba(var(--v-theme-main-blue), 0.06);
}

.MyMapResourceFilters__titleText {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-main-blue), 0.7);
  line-height: 1;
}

.MyMapResourceFilters__fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.MyMapResourceFilters__label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.MyMapResourceFilters__fieldCaption {
  font-size: var(--panel-label-size);
  color: rgb(var(--v-theme-main-blue));
}

.MyMapResourceFilters__input {
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: var(--dim-radius);
  border: 1px solid var(--neutral-border);
  background-color: rgb(var(--v-theme-surface));
  padding: 0.35rem 2rem 0.35rem 0.5rem;
  font-size: var(--panel-label-size);
  min-height: 2rem;
  font-family: inherit;
  font-weight: 500;
  font-style: normal;
  color: rgb(17, 24, 39);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 1rem;
  background-position: right 0.35rem center;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &--empty {
    color: rgb(156, 163, 175);
    font-style: italic;
    font-weight: 400;
  }

  &:hover {
    border-color: rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--v-theme-main-blue));
    box-shadow: 0 0 0 2px rgba(var(--v-theme-main-blue), 0.12);
  }
}

.MyMapResourceFilters__clear {
  align-self: flex-start;
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: inherit;
  border: none;
  border-radius: 0.75rem;
  background-color: var(--color-main);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-main-hover);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }
}
</style>
